import { useMemo } from 'react';
import { isArray, isString, omit } from 'lodash';

import { useTrackEvent } from '@/lib/analytics';
import { ensureValidUseOfVariables } from '@/core/exploration/variables';

import { SidebarSection } from '../exploration/sidebar-section';
import { VariableDefinitionForm } from './variable-definition-form';
import {
  EnumVariableDefinition,
  Exploration,
  ExplorationParameters,
  MultiselectVariableDefinition,
  VariableCell,
} from '../types';
import { useExplorationContext } from '../exploration/exploration-context';
import {
  getVariableDefaultOption,
  getVariableDefinitions,
  replaceVariableDefinition,
  replaceVariableKeys,
} from '../utils';

const ensureValidEnumParameterValue = (value: unknown, definition: EnumVariableDefinition) => {
  if (typeof value === 'number' || typeof value === 'string') {
    if (definition.source === 'static') {
      return definition.options.some(({ value: optionValue }) => optionValue === value)
        ? value
        : (definition.options.at(0)?.value ?? getVariableDefaultOption(definition));
    }
    return value;
  }

  if (isArray(value)) {
    if (definition.source === 'static') {
      return (
        value.find((valueItem) =>
          definition.options.some(({ value: optionValue }) => optionValue === valueItem),
        ) ??
        definition.options.at(0)?.value ??
        getVariableDefaultOption(definition)
      );
    }
    return value.find(isString) ?? '';
  }

  return '';
};

const ensureValidMultiselectParameterValue = (
  value: unknown,
  definition: MultiselectVariableDefinition,
) => {
  if (isArray(value)) {
    if (definition.source === 'static') {
      const retainedValues = value.filter((valueItem) =>
        definition.options.some(({ value }) => value === valueItem),
      );
      return retainedValues.length > 0
        ? retainedValues
        : definition.options.length > 0
          ? [definition.options[0].value]
          : [getVariableDefaultOption(definition)];
    }
    return value;
  }

  if (typeof value === 'number' || typeof value === 'string') {
    if (definition.source === 'static') {
      return definition.options.some(({ value: optionValue }) => optionValue === value)
        ? [value]
        : [];
    }
    return [value];
  }

  return [];
};

const ensureValidParameterValue = (
  value: unknown,
  definition: VariableCell['definition'],
): ExplorationParameters[string] => {
  switch (definition.kind) {
    case 'enum':
      return ensureValidEnumParameterValue(value, definition);
    case 'multiselect':
      return ensureValidMultiselectParameterValue(value, definition);
    default:
      return value as ExplorationParameters[string];
  }
};

interface EditVariableCellProps {
  cell: VariableCell;
  setExploration: (exploration: Exploration) => void;
}

export const EditVariableCell = (props: EditVariableCellProps) => {
  const { cell } = props;

  const { exploration, setExploration, parameters } = useExplorationContext();
  const trackEvent = useTrackEvent();

  const existingVariableKeys = useMemo(
    () => getVariableDefinitions(exploration).map((definition) => definition.key),
    [exploration],
  );

  const handleSubmit = (definition: VariableCell['definition']) => {
    trackEvent('Variable Updated', {
      explorationId: exploration.explorationId,
      name: exploration.name,
      definition,
    });

    const previousKey = cell.definition.key;
    const nextKey = definition.key;

    const updatedParemeters = {
      ...omit(parameters, previousKey),
      [nextKey]: ensureValidParameterValue(parameters[previousKey], definition),
    };

    const updatedExploration = ensureValidUseOfVariables(
      replaceVariableKeys(
        replaceVariableDefinition(definition, exploration, cell.id),
        previousKey,
        nextKey,
      ),
    );

    setExploration(updatedExploration, updatedParemeters);
  };

  return (
    <SidebarSection title="Variable">
      <VariableDefinitionForm
        key={cell.definition.key}
        definition={cell.definition}
        existingKeys={existingVariableKeys.filter((key) => key !== cell.definition.key)}
        onSubmit={handleSubmit}
      />
    </SidebarSection>
  );
};
